/**
 * Pure helpers for the technician view (tracker item 42): elapsed time,
 * Maps / tel links, card labels. No prices anywhere.
 */

import { SIGNATURE_MAX_DIMENSION, SIGNER_NAME_MAX, TECH_NOTE_MAX } from '../api/types';
import type { JobStatus, TechnicianJobCard } from '../api/types';

/** Today's date (YYYY-MM-DD) in an IANA timezone; falls back to UTC for an unknown name. */
export function todayInTimeZone(timeZone: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Whole seconds between two instants (never negative). */
export function elapsedSeconds(startedAt: string, now: Date | number = Date.now()): number {
  const start = Date.parse(startedAt);
  const end = typeof now === 'number' ? now : now.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.max(0, Math.floor((end - start) / 1000));
}

/** Timer face: "0:05:09" (hours are not padded). */
export function formatElapsed(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Short duration: "45m", "2h 05m", "0m". */
export function formatDurationShort(totalSeconds: number): string {
  const minutes = Math.floor(Math.max(0, totalSeconds) / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** "14:30" -> "2:30 PM"; anything else is returned as given. */
export function formatClock(hhmm: string | null | undefined): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
  if (!match) return hhmm ?? '';
  const hour = Number(match[1]);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 === 0 ? 12 : hour % 12}:${match[2]} ${suffix}`;
}

/** Card time window: "8:00 AM - 9:30 AM", "8:00 AM", or "All day" without a start time. */
export function timeWindowLabel(card: Pick<TechnicianJobCard, 'scheduledStartTime' | 'scheduledEndTime'>): string {
  if (!card.scheduledStartTime) return 'All day';
  const start = formatClock(card.scheduledStartTime);
  return card.scheduledEndTime ? `${start} - ${formatClock(card.scheduledEndTime)}` : start;
}

/** "Arrive within 30 min" / "Arrive within 2 h"; null without a window. */
export function arrivalWindowLabel(minutes: number | null | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  if (minutes % 60 === 0) return `Arrive within ${minutes / 60} h`;
  return `Arrive within ${minutes} min`;
}

/** One-line address: "12 Oak St, Springfield, IL 62701". */
export function formatAddress(property: Pick<TechnicianJobCard['property'], 'address' | 'city' | 'state' | 'zip'>): string {
  const region = [property.state, property.zip].filter((v) => v && v.trim()).join(' ');
  return [property.address, property.city, region].filter((v) => v && v.trim()).join(', ');
}

/** Google Maps search link for the address (opens the maps app on a phone). */
export function mapsUrl(property: Pick<TechnicianJobCard['property'], 'address' | 'city' | 'state' | 'zip'>): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatAddress(property))}`;
}

/** `tel:` link, or null when the phone has too few digits to dial. A leading + is kept. */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 3) return null;
  return `tel:${phone.trim().startsWith('+') ? '+' : ''}${digits}`;
}

export type CardTone = 'gray' | 'blue' | 'yellow' | 'green' | 'red';

const CARD_STATUS: Partial<Record<JobStatus, { label: string; tone: CardTone }>> = {
  DRAFT: { label: 'Draft', tone: 'gray' },
  WORK_ORDER_GENERATED: { label: 'Ready', tone: 'blue' },
  SCHEDULED: { label: 'Scheduled', tone: 'blue' },
  IN_PROGRESS: { label: 'In progress', tone: 'yellow' },
  COMPLETED: { label: 'Done', tone: 'green' },
  INVOICED: { label: 'Done', tone: 'green' },
  CANCELLED: { label: 'Cancelled', tone: 'red' },
};

/** Status badge text and colour; a job with a running timer shows as "Timer running". */
export function cardStatus(card: Pick<TechnicianJobCard, 'status' | 'openTimeEntries'>, runningUserId?: string): { label: string; tone: CardTone } {
  const base = CARD_STATUS[card.status] ?? { label: card.status, tone: 'gray' as CardTone };
  const running = runningUserId ? card.openTimeEntries.some((e) => e.userId === runningUserId) : false;
  return running && card.status === 'IN_PROGRESS' ? { label: 'Timer running', tone: 'yellow' } : base;
}

/** "2/5" checklist progress; null when the job has no steps. */
export function checklistProgressLabel(checklist: { total: number; completed: number } | null | undefined): string | null {
  if (!checklist || checklist.total <= 0) return null;
  return `${checklist.completed}/${checklist.total}`;
}

/** A finished (done) job is read-only on the job screen. */
export function isDoneStatus(status: JobStatus | string): boolean {
  return status === 'COMPLETED' || status === 'INVOICED';
}

/** Start is offered on jobs that are not draft, done or cancelled. */
export function canStartStatus(status: JobStatus | string): boolean {
  return status === 'WORK_ORDER_GENERATED' || status === 'SCHEDULED' || status === 'IN_PROGRESS';
}

// ============ Phase 3: tech notes, signature ============

/**
 * Same cleaning as the backend `cleanText`: NFC, control characters (line
 * breaks included) become spaces, bidi controls are removed, runs of spaces
 * collapse, trimmed.
 */
export function cleanTechText(value: string): string {
  let result = '';
  for (const ch of value.normalize('NFC')) {
    const code = ch.codePointAt(0) ?? 0;
    const control = code < 0x20 || (code >= 0x7f && code <= 0x9f) || code === 0x2028 || code === 0x2029;
    const bidi = code === 0x200e || code === 0x200f || (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069);
    if (control) result += ' ';
    else if (!bidi) result += ch;
  }
  return result.replace(/ {2,}/g, ' ').trim();
}

/** Why a tech note cannot be sent (null when fine). */
export function techNoteError(text: string): string | null {
  const clean = cleanTechText(text);
  if (!clean) return 'Enter a note.';
  if (clean.length > TECH_NOTE_MAX) return `A note may be at most ${TECH_NOTE_MAX} characters.`;
  return null;
}

/** Why a printed signer name cannot be used (null when fine). */
export function signerNameError(name: string): string | null {
  const clean = cleanTechText(name);
  if (!clean) return "Enter the signer's printed name.";
  if (clean.length > SIGNER_NAME_MAX) return `The name may be at most ${SIGNER_NAME_MAX} characters.`;
  return null;
}

/** "HH:MM" (24 h) in an IANA timezone; falls back to UTC for an unknown name. */
export function timeInTimeZone(timeZone: string, now: Date = new Date()): string {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  } catch {
    return now.toISOString().slice(11, 16);
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('hour') === '24' ? '00' : get('hour')}:${get('minute')}`;
}

/** The line the server appends for a tech note: `[YYYY-MM-DD HH:MM email] text`. */
export function formatTechNoteLine(now: Date, timeZone: string, author: string, text: string): string {
  return `[${todayInTimeZone(timeZone, now)} ${timeInTimeZone(timeZone, now)} ${cleanTechText(author)}] ${text}`;
}

/**
 * Backing-store size of the signature canvas: CSS size times the device pixel
 * ratio, rounded, at least 1 px and never more than 4096 px a side (the
 * server limit); the aspect ratio is kept when a side is capped.
 */
export function signatureCanvasSize(cssWidth: number, cssHeight: number, pixelRatio: number): { width: number; height: number } {
  const ratio = Number.isFinite(pixelRatio) && pixelRatio > 0 ? pixelRatio : 1;
  let width = Math.max(1, Math.round(cssWidth * ratio));
  let height = Math.max(1, Math.round(cssHeight * ratio));
  const scale = Math.min(1, SIGNATURE_MAX_DIMENSION / width, SIGNATURE_MAX_DIMENSION / height);
  if (scale < 1) {
    width = Math.max(1, Math.floor(width * scale));
    height = Math.max(1, Math.floor(height * scale));
  }
  return { width, height };
}

/** Friendly text for a failed signature save / finish (by backend error code); null for other errors. */
export function signatureErrorMessage(code: string | undefined): string | null {
  switch (code) {
    case 'SIGNATURE_NOT_PNG': return 'The signature image was not accepted as a PNG. Clear it and sign again.';
    case 'SIGNATURE_TOO_LARGE': return 'The signature image is too large. Clear it and sign again with a shorter signature.';
    case 'SIGNATURE_EMPTY': return 'The signature is empty. Ask the customer to sign first.';
    case 'SIGNATURE_REQUIRED': return 'Sign in the box first.';
    case 'SIGNER_NAME_REQUIRED': return "Enter the signer's printed name.";
    case 'JOB_CANCELLED': return 'This job is cancelled, so it cannot be signed.';
    case 'INVALID_UPLOAD': return 'The signature could not be uploaded. Try again.';
    default: return null;
  }
}

/**
 * Signed-at time for display, locale aware. With the company IANA timezone it
 * matches the tech note stamps; an unknown name falls back to the browser's.
 */
export function formatSignedAt(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };
  if (timeZone) {
    try {
      return date.toLocaleString(undefined, { ...options, timeZone });
    } catch {
      // unknown timezone name: fall through to the browser's
    }
  }
  return date.toLocaleString(undefined, options);
}
